import { useEffect, useState } from "react";
import { api, formatDateFull, formatRelative, todayISO } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, Clock, AlertCircle, FileText, Download, UserX, Users, TrendingUp, Mail } from "lucide-react";
import { toast } from "sonner";
import ReportCard from "@/components/ReportCard";

export default function DashboardPage() {
  const { isAdmin, user } = useAuth();
  const [data, setData] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingReminder, setSendingReminder] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [t, w] = await Promise.all([
          api.get("/reports/today"),
          api.get("/stats/weekly"),
        ]);
        setData(t.data);
        setWeekly(w.data.items || []);
      } else {
        const t = await api.get("/reports/mine/today");
        setData(t.data);
      }
    } catch (err) {
      toast.error("Veri yüklenemedi");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isAdmin]);

  const downloadPDF = async () => {
    try {
      const r = await api.get("/reports/weekly-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `haftalik-rapor-${todayISO()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF indirilemedi"); }
  };

  const sendReminderNow = async () => {
    if (!window.confirm("Rapor yazmayan tüm çalışanlara hatırlatma e-postası gönderilsin mi?")) return;
    setSendingReminder(true);
    try {
      const r = await api.post("/reminders/send-now");
      toast.success(`${r.data.sent || 0} e-posta gönderildi`);
    } catch { toast.error("E-posta gönderilemedi"); }
    finally { setSendingReminder(false); }
  };

  if (loading) return <div className="loading-block">Yükleniyor…</div>;

  if (!isAdmin) {
    // Employee home — direct summary of today
    return (
      <>
        <div className="page-heading compact-heading">
          <div>
            <p className="eyebrow">Günlük Rapor · Hoş geldiniz {user.name.split(" ")[0]}</p>
            <h1>{formatDateFull(todayISO())}</h1>
            <p className="subheading">Bugünkü raporunuzu paylaşın — ekibiniz ve yöneticiniz görecek.</p>
          </div>
        </div>
        {data?.exists ? (
          <div style={{ marginTop: 20 }}>
            <div className="status-strip">
              <span className="status-dot"/> Bugünkü raporunuzu paylaştınız · {formatRelative(data.updated_at || data.created_at)}
            </div>
            <div style={{ marginTop: 16 }}>
              <ReportCard report={data} onChanged={load} />
            </div>
          </div>
        ) : (
          <div className="empty-state" style={{ marginTop: 30 }}>
            <FileText size={30}/>
            <h3>Henüz bugün için rapor yazmadınız</h3>
            <p>Sol menüden "Günlük Rapor"a tıklayıp bugünün özetini paylaşabilirsiniz.</p>
          </div>
        )}
      </>
    );
  }

  // Admin dashboard
  const totalEmp = data?.total_users || 0;
  const reported = (data?.reports || []).filter(r => r.author?.email !== user.email).length;
  const missing = data?.missing || [];
  const avgDays = weekly.length ? Math.round(weekly.reduce((s, u) => s + u.days, 0) / weekly.length * 10) / 10 : 0;
  const totalHours = weekly.reduce((s, u) => s + u.hours, 0);

  return (
    <>
      <div className="page-heading compact-heading">
        <div>
          <p className="eyebrow">Yönetim Paneli · {formatDateFull(todayISO())}</p>
          <h1>Günlük takip</h1>
          <p className="subheading">Bugün hangi çalışan ne raporladı, kim yazmadı ve haftalık özet buradan izlenir.</p>
        </div>
        <div className="heading-actions">
          <button className="ghost-button" onClick={sendReminderNow} disabled={sendingReminder || missing.length === 0} data-testid="send-reminder-btn">
            <Mail size={16}/> {sendingReminder ? "Gönderiliyor…" : `Hatırlat (${missing.length})`}
          </button>
          <button className="ghost-button" onClick={downloadPDF} data-testid="download-weekly-pdf">
            <Download size={16}/> Haftalık PDF
          </button>
        </div>
      </div>

      <div className="metrics-grid">
        <div className="metric">
          <div className="metric-icon green"><CheckCircle2 size={18}/></div>
          <div className="metric-copy"><span>Bugün rapor veren</span><strong>{reported} / {totalEmp}</strong></div>
        </div>
        <div className="metric">
          <div className="metric-icon red"><UserX size={18}/></div>
          <div className="metric-copy"><span>Henüz rapor yok</span><strong>{missing.length}</strong></div>
        </div>
        <div className="metric">
          <div className="metric-icon amber"><Clock size={18}/></div>
          <div className="metric-copy"><span>Haftalık ortalama</span><strong>{avgDays} / 7 gün</strong></div>
        </div>
        <div className="metric">
          <div className="metric-icon">
            <TrendingUp size={18}/>
          </div>
          <div className="metric-copy"><span>Haftalık toplam</span><strong>{totalHours.toFixed(1)} saat</strong></div>
        </div>
      </div>

      {/* Missing reports */}
      {missing.length > 0 && (
        <div className="panel" style={{ marginTop: 24, padding: 20 }} data-testid="missing-reports-panel">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <AlertCircle size={16} style={{ color: "#f97316" }}/>
            <strong>Henüz rapor yazmayanlar</strong>
            <span className="cell-muted" style={{ marginLeft: 8 }}>({missing.length} kişi)</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {missing.map((m) => (
              <span key={m.id} className="person-chip" data-testid={`missing-${m.id}`}>
                <span className="chip-avatar">{m.name.charAt(0)}</span>
                <span>{m.name}<small>{m.position || "—"}</small></span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Today's reports */}
      <div style={{ marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <Users size={16} style={{ color: "#10b981" }}/>
          <h2 style={{ margin: 0, fontSize: 18 }}>Bugünkü raporlar</h2>
          <span className="cell-muted" style={{ marginLeft: 4 }}>({(data?.reports || []).length})</span>
        </div>
        {(data?.reports || []).length === 0 ? (
          <div className="empty-state">
            <FileText size={28}/>
            <h3>Henüz hiç kimse bugün rapor yazmadı</h3>
            <p>Çalışanlar rapor yazdıkça burada görünecek.</p>
          </div>
        ) : (
          <div className="reports-grid" data-testid="today-reports">
            {data.reports.map((r) => <ReportCard key={r.id} report={r} onChanged={load} />)}
          </div>
        )}
      </div>

      {/* Weekly stats table */}
      <div className="panel table-panel full-table" style={{ marginTop: 32 }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #1e293b", display: "flex", alignItems: "center", gap: 10 }}>
          <TrendingUp size={16} style={{ color: "#10b981" }}/>
          <strong>Son 7 gün — Çalışan özeti</strong>
        </div>
        <div className="data-table-wrap">
          <table data-testid="weekly-table">
            <thead><tr>
              <th>Çalışan</th><th>Pozisyon</th><th className="right">Rapor günü</th>
              <th className="right">Toplam saat</th><th>Oran</th>
            </tr></thead>
            <tbody>
              {weekly.length === 0 && (
                <tr><td colSpan="5" style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>Çalışan yok</td></tr>
              )}
              {weekly.map((u) => (
                <tr key={u.user_id} data-testid={`weekly-row-${u.user_id}`}>
                  <td className="bold-cell">{u.name}</td>
                  <td>{u.position || <span className="cell-muted">—</span>}</td>
                  <td className="right">{u.days} / 7</td>
                  <td className="right amount">{u.hours.toFixed(1)}</td>
                  <td>
                    <div className="bar"><div className="bar-fill" style={{ width: `${u.pct}%`, background: u.pct >= 70 ? "#10b981" : u.pct >= 40 ? "#f59e0b" : "#ef4444" }}/></div>
                    <small style={{ color: "#8390a5", fontSize: 11 }}>%{u.pct}</small>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
